/**
 * adaptStep2Props.ts
 *
 * Adapta el estado del pipeline WizardPageV2 (ExtractionResult +
 * ProcessedInfrastructure[]) al formato que espera Step2Page.
 *
 * Propósito único: transformación de datos. Sin efectos secundarios.
 *
 * @module wizard-v2/step2-valida/adaptStep2Props
 * @version 1.0.0 - Conexión Step2Page al wizard real
 */

import type { ExtractionResult } from '../../Step1';
import type { ProcessedInfrastructure } from '../../../types/processing';
import type { Step2PageProps, DocumentContext, ProgressData } from './Step2Page';
import { contarEstadosFila } from '../shared/estadoFila';
// S4PODA (11-sep-2026): los tipos del árbol de pestañas podado viven en `./types`.
import type {
  InfrastructureData,
  SinGeoFilterCounts as FilterCounts,
  ValidationCounts,
  ReviewItem,
  AbreviaturaData,
  DiccionarioEntry,
  DiccionarioCategory,
  CorrectionData,
  SuggestionData,
  SuggestionType,
} from './types';
import { getInfrastructureType } from '../../Step2/Step2MapAdapter';
import type { TypologyKey } from '../../Step2/types';
import { toWGS84 } from '../../../lib/leaflet-crs';

// ============================================================================
// HELPERS INTERNOS
// ============================================================================

/**
 * B06-FIX: Detecta si un nombre de municipio es una abreviatura extraída del
 * nombre de fichero y resuelve el nombre completo desde las infraestructuras.
 *
 * Condiciones de abreviatura:
 *   1. Termina en punto (ej. «Alb.», «Cól.»)
 *   2. Longitud < 4 caracteres (ej. «Gr», «Al»)
 *
 * Fallback cascade:
 *   1. allInfras[0].municipio — suele contener el nombre completo del documento
 *   2. rawMunicipality — valor original aunque sea abreviatura
 */
function resolveFullMunicipio(
  rawMunicipality: string | undefined,
  allInfras: Array<{ municipio?: string }>,
): string {
  const raw = rawMunicipality ?? '';
  const isAbbreviation = raw.endsWith('.') || (raw.length > 0 && raw.length < 4);
  if (!isAbbreviation) return raw;

  // Buscar primer municipio no vacío en las infraestructuras parseadas
  const fromDoc = allInfras.find(i => i.municipio && i.municipio.trim().length >= 4)?.municipio;
  return fromDoc ?? raw;
}

/**
 * Clasifica una infraestructura como «sin geolocalizar».
 *
 * BUG-2 FIX: añadido criterio por flag de validación cruzada.
 * BAJA_CONFIANZA: geocodificador encontró algo con confianza <70 y asigna
 * coordenadasFinales igualmente → xFinal tiene valor, pero no es fiable.
 * SIN_COORDENADAS: sin coords en doc ni referencia, xFinal=undefined.
 * Ambos casos deben aparecer en «Sin geolocalizar» para que el técnico
 * pueda revisarlos, aunque tengan coords tentativas visibles en el mapa.
 *
 * C3-FIX mantenido: score NO se usa para clasificar.
 */
function isSinGeo(infra: ProcessedInfrastructure): boolean {
  // P2-S3: elemento confirmado por el técnico → validación humana cierra la
  // revisión (sale de la lista, suma a «aprobados»; progreso ≈ AUDIT §7.4).
  if (infra.confirmado === true) return false;
  const flag = infra.crossValidation?.flag;
  // Coords de baja confianza o sin ninguna fuente → requieren revisión manual
  if (flag === 'BAJA_CONFIANZA' || flag === 'SIN_COORDENADAS') return true;
  return !(
    typeof infra.xFinal === 'number' &&
    typeof infra.yFinal === 'number' &&
    infra.xFinal !== 0 &&
    infra.yFinal !== 0
  );
}

/**
 * Mapea ProcessedInfrastructure → InfrastructureData (UI SinGeolocalizarContent).
 * El código INE viene del metadata del documento, no de la infraestructura.
 */
function toInfrastructureData(
  infra: ProcessedInfrastructure,
  ineCode: string,
): InfrastructureData {
  // BUG-12 FIX: convertir xFinal/yFinal a WGS84 para usarlos como originalCoords
  // en ExpandedContent cuando candidates=[] (infras NO_GEOCODIFICABLE con coords del ODT).
  let latWgs84: number | undefined;
  let lngWgs84: number | undefined;
  if (typeof infra.xFinal === 'number' && typeof infra.yFinal === 'number'
      && infra.xFinal !== 0 && infra.yFinal !== 0) {
    try {
      const [lat, lng] = toWGS84(infra.xFinal, infra.yFinal);
      latWgs84 = lat;
      lngWgs84 = lng;
    } catch {
      // toWGS84 puede fallar si las coords están fuera del elipsoide — ignorar
    }
  }

  return {
    id: infra.id,
    name: infra.nombre ?? '',
    address: infra.direccion ?? infra.direccionAlt ?? '',
    typology: (infra.tipo ?? 'OTRO') as InfrastructureData['typology'],
    municipio: infra.municipio ?? '',
    provincia: infra.provincia ?? '',
    ine: ineCode,
    originalScore: infra.score ?? 0,
    currentScore: infra.score ?? 0,
    candidates: [],
    ptelOriginalName: infra.nombre ?? undefined,
    infraType: getInfrastructureType(infra.tipo as TypologyKey),
    latWgs84,
    lngWgs84,
  };
}

/**
 * Calcula FilterCounts para el tab «Sin Geolocalizar».
 *
 * BUG-09 FIX: rangos alineados con FILTER_PILLS de SinGeolocalizarContent:
 *   critical → 0-49  (antes: solo score===0)
 *   low      → 50-69 (antes: 1-29)
 *   medium   → 70-84 (antes: 30-69)
 *   high     → 85-100 (antes: 70-100)
 */
function buildSinGeoFilterCounts(sinGeo: ProcessedInfrastructure[]): FilterCounts {
  return {
    all:      sinGeo.length,
    critical: sinGeo.filter(i => (i.score ?? 0) < 50).length,
    low:      sinGeo.filter(i => (i.score ?? 0) >= 50 && (i.score ?? 0) < 70).length,
    medium:   sinGeo.filter(i => (i.score ?? 0) >= 70 && (i.score ?? 0) < 85).length,
    high:     sinGeo.filter(i => (i.score ?? 0) >= 85).length,
  };
}

/**
 * BUG-UI-16: Detecta grupos de infraestructuras que comparten exactamente las
 * mismas coordenadas UTM finales (xFinal/yFinal). Solo informa de grupos con
 * ≥ DUPLICATE_MIN_SIZE miembros para reducir ruido (pares legítimos existen).
 *
 * Complejidad O(n): una pasada con Map, clave «x|y» redondeada a entero.
 */
const DUPLICATE_MIN_SIZE = 3;

function buildDuplicateCoordsWarning(
  infras: ProcessedInfrastructure[],
): Array<{ x: number; y: number; names: string[] }> {
  const map = new Map<string, { x: number; y: number; names: string[] }>();
  for (const infra of infras) {
    if (typeof infra.xFinal !== 'number' || typeof infra.yFinal !== 'number') continue;
    if (infra.xFinal === 0 || infra.yFinal === 0) continue;
    const key = `${Math.round(infra.xFinal)}|${Math.round(infra.yFinal)}`;
    const entry = map.get(key) ?? { x: infra.xFinal, y: infra.yFinal, names: [] };
    entry.names.push(infra.nombre ?? `Infra ${infra.id}`);
    map.set(key, entry);
  }
  return Array.from(map.values()).filter(g => g.names.length >= DUPLICATE_MIN_SIZE);
}

/**
 * Contadores iniciales de tabs a partir de los resultados geocodificados.
 * ValidationCounts usa estructura anidada { approved, pending } para todos
 * los tabs salvo sinGeo (que es un número plano).
 */
function buildInitialCounts(
  all: ProcessedInfrastructure[],
  sinGeo: ProcessedInfrastructure[],
): Partial<ValidationCounts> {
  const revisarPending = all.filter(
    i =>
      i.crossValidation?.flag === 'REVISION_MANUAL' ||
      i.crossValidation?.flag === 'REVISION_MENOR',
  ).length;

  return {
    sinGeo: sinGeo.length,
    revisar: { approved: 0, pending: revisarPending },
    abrev: { approved: 0, pending: 0 },
    dict: { approved: 0, pending: 0 },
    correcciones: { approved: 0, pending: 0 },
    ia: { approved: 0, pending: 0 },
  };
}

// ============================================================================
// CONTRATO PÚBLICO
// ============================================================================

export interface AdaptStep2PropsInput {
  /** Resultado de la extracción del documento */
  extractionData: ExtractionResult;
  /**
   * Infraestructuras con geocodificación aplicada. Vacío cuando todas las
   * coords eran originales del documento (no se llamó geocodeNeeded).
   */
  geocodedResults: ProcessedInfrastructure[];
  /**
   * Infraestructuras tras el paso de normalización (score y xFinal/yFinal
   * calculados). Se usan como segundo fallback para evitar el cast inseguro
   * sobre ExtractedInfrastructure[] que carece de xFinal/yFinal/score (B07).
   */
  normalizedResults: ProcessedInfrastructure[];
  /** Callback que el wizard ejecuta cuando el técnico confirma → P3 */
  onContinue: () => void;
  /** BUG-7: abre el formulario de edición manual para la infra dada */
  onOpenManualEdit?: (infraId: string) => void;
}

/**
 * Adapta el estado del wizard al formato que espera Step2Page.
 *
 * Cascada de fuente de datos (de más a menos completa):
 *   1. geocodedResults   — resultado final del pipeline (coords + score)
 *   2. normalizedResults — coords originales normalizadas (score calculado)
 *   3. extractionData.infrastructures — crudos sin score (transitorio)
 *
 * El nivel 3 usa un cast inseguro: ExtractedInfrastructure no tiene
 * xFinal/yFinal/score. Solo se alcanza si el usuario navega a P2 antes
 * de que la normalización termine.
 *
 * Retorna null si no hay infraestructuras (carga incompleta).
 */
export function adaptStep2Props(input: AdaptStep2PropsInput): Step2PageProps | null {
  const { extractionData, geocodedResults, normalizedResults, onContinue, onOpenManualEdit } = input;
  const { metadata } = extractionData;

  // B07-FIX: usar normalizedResults como segundo fallback antes del cast
  // inseguro. geocodedResults.length === 0 ocurre cuando todas las coords
  // eran originales del documento — normalizedResults contiene los datos
  // correctos con xFinal, yFinal y score ya calculados.
  const allInfras: ProcessedInfrastructure[] =
    geocodedResults.length > 0
      ? geocodedResults
      : normalizedResults.length > 0
        ? normalizedResults
        : (extractionData.infrastructures as unknown as ProcessedInfrastructure[]);

  if (allInfras.length === 0) return null;

  const ineCode = metadata.ineCode ?? '';

  // --- DocumentContext ---
  // B06-FIX: resolver nombre completo cuando metadata.municipality es abreviatura
  // (termina en «.» o longitud < 4). Fallback: primer municipio de las infras parseadas.
  const municipioResolved = resolveFullMunicipio(metadata.municipality, allInfras);
  const documentContext: DocumentContext = {
    fileName: metadata.fileName ?? extractionData.file?.name ?? 'Documento',
    municipio: municipioResolved,
    provincia: metadata.province ?? '',
    ine: ineCode,
    totalElements: allInfras.length,
  };

  // --- Progreso ---
  // P2VOC-a: la cuenta sale de la clasificación única de la fila
  // (`shared/estadoFila`). `sinGeo` (BAJA_CONFIANZA, SIN_COORDENADAS o sin X/Y)
  // sigue alimentando solo los datos heredados `sinGeo*` de más abajo.
  const sinGeo = allInfras.filter(isSinGeo);
  const estados = contarEstadosFila(allInfras);

  const progress: ProgressData = {
    approved: estados.ubicada,
    pending: estados.por_revisar,
    notGeolocated: estados.sin_coordenada,
  };

  // --- BUG-UI-16: coords duplicadas ---
  const duplicateCoordsWarning = buildDuplicateCoordsWarning(allInfras);

  // --- Datos para el tab Sin Geolocalizar ---
  const sinGeoInfrastructures = sinGeo.map(i => toInfrastructureData(i, ineCode));
  const sinGeoFilterCounts = buildSinGeoFilterCounts(sinGeo);

  // --- Contadores de tabs ---
  const initialCounts = buildInitialCounts(allInfras, sinGeo);

  // --- Datos para tabs secundarias ---
  // C4-FIX: reviewItems construidos desde allInfras con flag REVISION_MANUAL/MENOR.
  // En esta fase del pipeline los campos crossValidation son opcionales y suelen
  // ser undefined → array vacío. Se poblará cuando F4 esté completo.
  const reviewItems: ReviewItem[] = allInfras
    .filter(
      i =>
        i.crossValidation?.flag === 'REVISION_MANUAL' ||
        i.crossValidation?.flag === 'REVISION_MENOR',
    )
    .map(i => ({
      id: i.id,
      infrastructure: i.nombre ?? '',
      field: 'coordX' as const,
      currentValue: String(i.xFinal ?? i.xOriginal ?? ''),
      problemType: 'low_confidence' as const,
      problemDescription: i.crossValidation?.flag ?? 'Revisión requerida',
      confidence: i.score ?? 0,
      status: 'pending' as const,
      coordinates:
        i.xFinal && i.yFinal ? { x: i.xFinal, y: i.yFinal } : undefined,
    }));

  // BUG-5: extraer datos reales de allInfras en lugar de arrays vacíos.

  // ── Abreviaturas ──────────────────────────────────────────────────────────
  // Detecta tokens ≤5 chars que terminan en '.' o son ≤3 chars todo-mayúsculas
  // aparecidos en nombre o dirección de cada infraestructura.
  const _abrevMap = new Map<string, { sources: string[]; count: number; infraIds: Set<string> }>();
  const _ABRV = /\b([A-ZÁÉÍÓÚÜÑ]{1,3}\.?|[A-Za-záéíóúüñ]{2,5}\.)\b/g;
  for (const infra of allInfras) {
    const fields = [infra.nombre ?? '', infra.direccion ?? ''];
    for (const text of fields) {
      let m: RegExpExecArray | null;
      while ((m = _ABRV.exec(text)) !== null) {
        const tok = m[1];
        const entry = _abrevMap.get(tok) ?? { sources: [], count: 0, infraIds: new Set<string>() };
        if (!entry.sources.includes(infra.nombre ?? '—')) {
          entry.sources.push(infra.nombre ?? '—');
        }
        entry.count++;
        entry.infraIds.add(infra.id);
        _abrevMap.set(tok, entry);
      }
    }
  }
  const abreviaturas: AbreviaturaData[] = Array.from(_abrevMap.entries())
    .filter(([, v]) => v.count >= 2)   // al menos 2 apariciones para reducir ruido
    .map(([tok, v], idx) => ({
      id: `abv-${idx}`,
      original: tok,
      expansion: tok.replace(/\.$/, ''),   // expansión mínima: quita el punto
      source: v.sources[0] ?? '—',
      occurrences: v.count,
      status: 'pending' as const,
      confidence: Math.min(95, 60 + v.count * 5),
      infraIds: Array.from(v.infraIds),
    }));

  // ── Diccionario ───────────────────────────────────────────────────────────
  // Registra términos únicos de nombre/dirección que no estén normalizados
  // (primera letra minúscula, o con caracteres no-ASCII distintos de tildes).
  const _dicMap = new Map<string, { cat: 'nombre' | 'direccion'; count: number; infraIds: Set<string> }>();
  const _NON_NORM = /[^A-Za-záéíóúüñÁÉÍÓÚÜÑ0-9 .,/()\-]/;
  for (const infra of allInfras) {
    const pairs: Array<[string, 'nombre' | 'direccion']> = [
      [infra.nombre ?? '', 'nombre'],
      [infra.direccion ?? '', 'direccion'],
    ];
    for (const [text, cat] of pairs) {
      if (!text) continue;
      // términos con char no normalizado
      if (_NON_NORM.test(text)) {
        const key = text.slice(0, 60);
        const entry = _dicMap.get(key) ?? { cat, count: 0, infraIds: new Set<string>() };
        entry.count++;
        entry.infraIds.add(infra.id);
        _dicMap.set(key, entry);
      }
      // nombre que empieza en minúscula (posible error de capitalización)
      if (cat === 'nombre' && /^[a-záéíóúüñ]/.test(text)) {
        const entry = _dicMap.get(text) ?? { cat, count: 0, infraIds: new Set<string>() };
        entry.count++;
        entry.infraIds.add(infra.id);
        _dicMap.set(text, entry);
      }
    }
  }
  const diccionarioEntries: DiccionarioEntry[] = Array.from(_dicMap.entries())
    .map(([term, v], idx) => ({
      id: `dic-${idx}`,
      original: term,
      normalized: term.charAt(0).toUpperCase() + term.slice(1),
      category: v.cat,
      source: term,
      occurrences: v.count,
      status: 'suggested' as const,
      confidence: 70,
      infraIds: Array.from(v.infraIds),
    }));

  // ── Correcciones ──────────────────────────────────────────────────────────
  // Infras cuya coordenada final difiere de la original (normalización o
  // geocodificación introdujo un cambio detectable).
  const corrections: CorrectionData[] = allInfras
    .filter(i => {
      const xChanged = i.xFinal !== undefined &&
        Math.abs(i.xFinal - parseFloat(i.xOriginal ?? '0')) > 0.5;
      const yChanged = i.yFinal !== undefined &&
        Math.abs(i.yFinal - parseFloat(i.yOriginal ?? '0')) > 0.5;
      return xChanged || yChanged;
    })
    .map((i, idx) => ({
      id: `cor-${idx}`,
      original: `X ${i.xOriginal ?? '—'} · Y ${i.yOriginal ?? '—'}`,
      corrected: `X ${i.xFinal?.toFixed(0) ?? '—'} · Y ${i.yFinal?.toFixed(0) ?? '—'}`,
      type: 'coords' as const,
      field: 'coordenadas',
      infrastructure: i.nombre ?? `Infra ${idx + 1}`,
      status: i.score >= 70 ? ('auto' as const) : ('pending' as const),
      confidence: i.score,
      // P2RED-S1-3a: id del elemento para la proyección al contrato `Propuesta`.
      infraId: i.id,
    }));

  // ── Sugerencias IA ────────────────────────────────────────────────────────
  // Infras sin geocodificar o con confianza baja que necesitan revisión.
  const suggestions: SuggestionData[] = allInfras
    .filter(i => i.confidence === 'BAJA' || i.confidence === 'NULA' || i.score < 50)
    .map((i, idx) => ({
      id: `sug-${idx}`,
      type: (i.xFinal === undefined ? 'complete' : 'improve') as SuggestionType,
      title: i.xFinal === undefined
        ? 'Coordenadas no encontradas'
        : 'Confianza baja — revisar coordenadas',
      description: i.crossValidation?.flag
        ? `Flag: ${i.crossValidation.flag}`
        : `Score ${i.score} · Confianza ${i.confidence}`,
      infrastructure: i.nombre ?? `Infra ${idx + 1}`,
      field: 'coordenadas',
      currentValue: i.xFinal ? `X ${i.xFinal.toFixed(0)} · Y ${i.yFinal?.toFixed(0)}` : '—',
      suggestedValue: 'Revisar en fuente primaria',
      confidence: i.score,
      status: 'new' as const,
      reason: i.crossValidation?.reviewReason ?? undefined,
      // P2RED-S1-3a: id del elemento para la proyección al contrato `Propuesta`.
      infraId: i.id,
    }));

  return {
    documentContext,
    progress,
    initialCounts,
    sinGeoInfrastructures,
    sinGeoFilterCounts,
    // C4: reviewItems reales (vacío si pipeline no genera flags aún)
    reviewItems,
    // C5: tabs secundarias vacías (se poblarán en F2/F4)
    abreviaturas,
    diccionarioEntries,
    corrections,
    suggestions,
    isDemo: false,
    onContinue,
    // BUG-7: propagar callback de edición manual si se proporciona
    ...(onOpenManualEdit !== undefined && { onOpenManualEdit }),
    // BUG-UI-16: grupos de coords duplicadas (vacío si no hay)
    duplicateCoordsWarning,
  };
}
